/**
 * Copyright (c) Siemens 2016 - 2026
 * SPDX-License-Identifier: MIT
 */
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentChecked,
  AfterContentInit,
  booleanAttribute,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  input,
  isSignal,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges
} from '@angular/core';
import {
  FormControl,
  NgControl,
  RequiredValidator,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { SiTranslatePipe, TranslatableString } from '@siemens/element-translate-ng/translate';

import { SiFormFieldsetComponent } from '../form-fieldset/si-form-fieldset.component';
import { SiFormFieldsetControl } from '../form-fieldset/si-form-fieldset.control';
import { SiFormContainerComponent } from '../si-form-container/si-form-container.component';
import { SI_FORM_ITEM_CONTROL, SiFormItemControl } from '../si-form-item.control';
import { SiFormValidationErrorMapper } from '../si-form-validation-error.model';
import { SiFormValidationErrorService } from '../si-form-validation-error.service';
import { SiFormFieldNativeControl } from './si-form-field-native.control';

/** @internal */
export interface SiFormError {
  key: string;
  message?: TranslatableString;
  params: any;
}

@Component({
  selector: 'si-form-item',
  imports: [SiTranslatePipe, NgTemplateOutlet],
  templateUrl: './si-form-item.component.html',
  styleUrl: '../si-form.shared.scss',
  host: {
    '[class.required]': 'required()',
    '[style.--si-form-label-width]': 'labelWidthCssVar()',
    '[class.form-check]': 'fieldControl()?.isFormCheck',
    '[class.form-check-inline]': 'fieldset?.inline()',
    '[class.si-form-input]': '!fieldset'
  }
})
export class SiFormItemComponent
  implements
    AfterContentInit,
    AfterContentChecked,
    OnChanges,
    OnInit,
    OnDestroy,
    SiFormFieldsetControl
{
  /**
   * The label to be displayed in the form item.
   * It will be translated if a translation key is available.
   */
  readonly label = input.required<TranslatableString>();

  /**
   * A custom width value to be applied to the label.
   * A value of 0 is allowed as well to visually hide away the label area.
   *
   * Numbers will be converted to pixels.
   * Using numbers is discouraged and might be dropped.
   *
   * @example labelWidth="100px"
   */
  readonly labelWidth = input<string | number>();

  /**
   * Disables the automatic error printing. Error printing will be enabled by default in v46.
   *
   * @defaultValue false
   */
  readonly disableErrorPrinting = input(false, { transform: booleanAttribute });

  /** Maps validation errors of the contained control to custom messages. */
  readonly formErrorMapper = input<SiFormValidationErrorMapper>();

  /**
   * Defines that this form item is required for the overall form to be valid.
   *
   * @defaultValue false
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly requiredInput = input(false, { transform: booleanAttribute, alias: 'required' });

  protected readonly fieldControlQuery = contentChild(SI_FORM_ITEM_CONTROL, { descendants: true });
  /** @internal */
  readonly ngControl = contentChild(NgControl, { descendants: true });
  protected readonly controlElementRef = contentChild<NgControl, ElementRef<HTMLElement>>(
    NgControl,
    {
      read: ElementRef,
      descendants: true
    }
  );
  protected readonly requiredValidator = contentChild(RequiredValidator, { descendants: true });

  /** @internal */
  readonly errors = signal<SiFormError[]>([]);

  /** @internal */
  readonly touched = signal(false);

  protected fieldset = inject(SiFormFieldsetComponent, { optional: true });
  protected container = inject(SiFormContainerComponent, { optional: true });

  protected get formItemId(): string | null {
    const fieldControl = this.fieldControl();
    return isSignal(fieldControl?.id) ? (fieldControl.id() ?? null) : (fieldControl?.id ?? null);
  }

  protected get formItemLabelledBy(): string | null {
    const fieldControl = this.fieldControl();
    return isSignal(fieldControl?.labelledby)
      ? (fieldControl.labelledby() ?? null)
      : (fieldControl?.labelledby ?? null);
  }

  protected get formItemErrormessageId(): string | null {
    const fieldControl = this.fieldControl();
    return isSignal(fieldControl?.errormessageId)
      ? (fieldControl?.errormessageId() ?? null)
      : (fieldControl?.errormessageId ?? null);
  }

  protected readonly fieldControl = computed(
    () => this.fieldControlQuery() ?? this.fieldControlNative()
  );

  private validationErrorService = inject(SiFormValidationErrorService);
  private requiredTestControl = new FormControl('');
  private validator?: ValidatorFn | null;
  private previousErrors?: ValidationErrors | null;
  private readonly hasRequiredValidator = signal(false);
  private readonly fieldControlNative = signal<SiFormItemControl | undefined>(undefined);

  protected readonly labelWidthCssVar = computed(() => {
    const labelWidth = this.labelWidth();
    if (typeof labelWidth === 'number') {
      return `${labelWidth}px`;
    }

    return labelWidth;
  });

  protected readonly printErrors = computed(() => {
    return !this.disableErrorPrinting() && !(this.container?.disableErrorPrinting() ?? false);
  });

  /** @internal */
  readonly required = computed(() => this.requiredInput() || this.hasRequiredValidator());

  /** @internal */
  readonly control = computed(() => this.ngControl()?.control);

  /** @internal */
  readonly radioGroupName = computed(() => {
    const element = this.controlElementRef()?.nativeElement;
    if (element?.tagName === 'INPUT') {
      const inputElement = element as HTMLInputElement;
      return inputElement.type === 'radio' ? inputElement.name || undefined : undefined;
    }

    return undefined;
  });

  ngOnChanges(changes: SimpleChanges<this>): void {
    if (changes.formErrorMapper) {
      this.updateValidationMessages();
    }
  }

  ngOnInit(): void {
    this.fieldset?.registerControl(this);
  }

  ngAfterContentInit(): void {
    if (!this.fieldControlQuery()) {
      this.fieldControlNative.set(
        SiFormFieldNativeControl.createForElementRef(this.controlElementRef())
      );
    }
  }

  ngAfterContentChecked(): void {
    this.updateRequiredState();
    this.updateValidationMessages();
    this.touched.set(!!this.ngControl()?.touched);
  }

  ngOnDestroy(): void {
    this.fieldset?.unregisterControl(this);
  }

  private updateRequiredState(): void {
    const requiredValidator = this.requiredValidator();
    if (requiredValidator) {
      // the easy case: required validator is applied in the template: <input required>
      this.hasRequiredValidator.set(booleanAttribute(requiredValidator.required));
    } else {
      // No required validator is applied via template, it could still be applied in the code.
      // If validators are applied via code, the validator object will change of controls.
      // So we only need to check for required if the validator object was changed.
      const validator = this.ngControl()?.control?.validator;

      if (this.validator !== validator) {
        this.validator = validator;
        this.requiredTestControl.setValidators(this.validator ?? null);
        this.requiredTestControl.updateValueAndValidity();
        this.hasRequiredValidator.set(this.requiredTestControl.errors?.required ?? false);
      }
    }
  }

  private updateValidationMessages(): void {
    const control = this.ngControl();
    const currentErrors = control?.errors;
    if (this.previousErrors !== currentErrors) {
      if (control && this.printErrors()) {
        this.errors.set(
          this.validationErrorService
            .resolveErrors(
              control.name,
              control.errors,
              this.formErrorMapper(),
              this.container?.formErrorMapper()
            )
            .filter(error => error.message)
        );
        this.previousErrors = currentErrors;
      } else if (this.errors().length) {
        this.errors.set([]);
      }
    }
  }
}
